import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";

interface FiringAlert {
  name: string | null;
  service: string | null;
  severity: string | null;
}

interface NoisiestAlert {
  alertname: string | null;
  service: string | null;
  severity: string | null;
  score: number;
}

interface AlertHealthResponse {
  status: "ok" | "unavailable";
  firing: {
    total: number;
    by_severity: { critical: number; warning: number; info: number };
  };
  runbook_coverage_pct: number | null;
  missing_runbook: FiringAlert[];
  missing_owner: FiringAlert[];
  noisiest_24h: NoisiestAlert[];
  silences_active: number;
}

const TITLE = "Alert Health";
const DESC = "Firing alerts, runbook & owner coverage, and the noisiest signals over 24h.";

// Severity → inline color. Reuses the same palette tokens as .tier-badge / .kind-tag.
const SEV_COLOR: Record<string, string> = {
  critical: "var(--red)",
  warning: "var(--yellow)",
  info: "var(--info)",
};

function SeverityBadge({ severity }: { severity: string | null }) {
  const sev = severity ?? "info";
  const color = SEV_COLOR[sev] ?? "var(--gray)";
  return (
    <span
      className="tier-badge"
      style={{ color, border: `1px solid ${color}55`, background: "transparent" }}
    >
      {sev}
    </span>
  );
}

export function AlertHealthTab() {
  const { data, loading, error } = useApi<AlertHealthResponse>("/command-center/alerts/health");

  if (loading) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="loading"><div className="loading-spinner" /><p>Checking alert health…</p></div>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <ContentHeader title={TITLE} description={DESC} />
        <div className="empty">Could not load alert health. Is command-center-api running?</div>
      </>
    );
  }

  const cov = data.runbook_coverage_pct;
  const covCls = cov == null ? "warn" : cov >= 90 ? "ok" : "warn";
  const crit = data.firing.by_severity.critical;
  const warn = data.firing.by_severity.warning;

  return (
    <>
      <ContentHeader title={TITLE} count={data.firing.total} description={DESC} />

      {data.status === "unavailable" && (
        <div className="empty" style={{ marginBottom: 18, padding: "14px 18px", textAlign: "start" }}>
          AlertManager is unreachable — showing empty state. Set <code>ALERTMANAGER_URL</code> on the API.
        </div>
      )}

      <div className="cov-summary">
        <span className={`cov-pill ${crit ? "crit" : "ok"}`}>
          <span className="dot" /><strong>{crit}</strong> critical
        </span>
        <span className={`cov-pill ${warn ? "warn" : "ok"}`}>
          <span className="dot" /><strong>{warn}</strong> warning
        </span>
        <span className={`cov-pill ${covCls}`}>
          <span className="dot" /><strong>{cov == null ? "—" : `${cov}%`}</strong> runbook coverage
        </span>
        <span className="cov-pill ok">
          <span className="dot" /><strong>{data.silences_active}</strong> silences active
        </span>
      </div>

      <h3 style={{ margin: "22px 0 10px", fontSize: 13, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        Noisiest alerts (24h)
      </h3>
      {data.noisiest_24h.length === 0 ? (
        <div className="empty">No firing-sample data — needs <code>GRAFANA_TOKEN</code> on the API.</div>
      ) : (
        <table className="cov-matrix">
          <thead>
            <tr><th>Alert</th><th>Service</th><th>Severity</th><th>Score</th></tr>
          </thead>
          <tbody>
            {data.noisiest_24h.map((a, i) => (
              <tr key={`${a.alertname ?? "?"}-${a.service ?? "?"}-${i}`}>
                <td>{a.alertname ?? "—"}</td>
                <td>{a.service ?? "—"}</td>
                <td><SeverityBadge severity={a.severity} /></td>
                <td>{a.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AlertList title="Firing alerts missing a runbook" alerts={data.missing_runbook} />
      <AlertList title="Firing alerts missing an owner" alerts={data.missing_owner} />
    </>
  );
}

function AlertList({ title, alerts }: { title: string; alerts: FiringAlert[] }) {
  return (
    <>
      <h3 style={{ margin: "22px 0 10px", fontSize: 13, color: "var(--text-muted)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {title}
      </h3>
      {alerts.length === 0 ? (
        <div className="empty">None — all firing alerts are covered.</div>
      ) : (
        <table className="cov-matrix">
          <thead>
            <tr><th>Alert</th><th>Service</th><th>Severity</th></tr>
          </thead>
          <tbody>
            {alerts.map((a, i) => (
              <tr key={`${a.name ?? "?"}-${a.service ?? "?"}-${i}`}>
                <td>{a.name ?? "—"}</td>
                <td>{a.service ?? "—"}</td>
                <td><SeverityBadge severity={a.severity} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
